/** Safety ceiling for reviews analyzed per GenAI widget scope (matches API welcome_genai_max_analysis_reviews). */
export const WELCOME_GENAI_ANALYSIS_REVIEW_LIMIT = 100000;

export function isWelcomeGenaiReviewScopeCapped(
  scopeTotal,
  limit = WELCOME_GENAI_ANALYSIS_REVIEW_LIMIT,
) {
  const total = Number(scopeTotal);
  const cap = Number(limit);
  return Number.isFinite(total) && Number.isFinite(cap) && total > cap;
}

function parseFiniteCount(raw) {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** True when summary tab is a per-topic Highlights row (not General/global). */
export function isWelcomeHierarchicalTopicTab(tab) {
  const tabKey = String(tab?.id ?? "").trim().toLowerCase();
  return (
    !!tabKey &&
    tabKey !== "general" &&
    tabKey !== "global" &&
    (tabKey.startsWith("topic-") || tabKey.startsWith("topic_"))
  );
}

export { parseFiniteCount as parseWelcomeFiniteCount };

/** Resolve GenAI review scope fields from widget KPI payload or widget row. */
export function resolveWelcomeReviewScopeFromData(data, widget) {
  const scopeTotal = parseFiniteCount(
    data?.scope_review_count_total ?? widget?.review_count_total,
  );
  let analyzed = parseFiniteCount(
    data?.source_review_count ?? data?.review_count,
  );
  // Stale Mongo source_review_count can exceed dashboard scope (snippet rows vs reviews).
  if (
    scopeTotal != null &&
    analyzed != null &&
    analyzed > scopeTotal
  ) {
    analyzed = scopeTotal;
  }
  const capped = data?.review_analysis_capped === true;
  const limit =
    parseFiniteCount(data?.review_analysis_limit) ??
    WELCOME_GENAI_ANALYSIS_REVIEW_LIMIT;
  return { analyzed, scopeTotal, capped, limit };
}

/**
 * Authoritative review count for UI labels (footer links, tab headers).
 * Prefers dashboard scope over stale worker/Mongo snapshots.
 */
export function resolveWelcomeDisplayReviewCount(data, widget, extras = {}) {
  const scope = resolveWelcomeReviewScopeFromData(data, widget);
  if (scope.scopeTotal != null) {
    return Math.max(0, Math.round(scope.scopeTotal));
  }
  if (scope.analyzed != null) {
    return Math.max(0, Math.round(scope.analyzed));
  }
  for (const key of [
    "review_count",
    "num_of_reviews",
    "total_reviews",
  ]) {
    const n = parseFiniteCount(extras[key] ?? data?.[key]);
    if (n != null) return Math.max(0, Math.round(n));
  }
  const tabCount = parseFiniteCount(
    extras.tabReviewCount ?? extras.tab_review_count,
  );
  if (tabCount != null) return Math.max(0, Math.round(tabCount));
  return null;
}

/** User-facing line: capped scope vs simple review count. */
export function formatWelcomeReviewScopeLabel(t, nf, scope) {
  if (!scope) return null;
  const { analyzed, scopeTotal, limit } = scope;
  const isCapped =
    scopeTotal != null &&
    analyzed != null &&
    scopeTotal > analyzed &&
    analyzed > 0;
  if (isCapped) {
    return t("welcome.reviewScopeCapped", {
      analyzed: nf.format(analyzed),
      total: nf.format(scopeTotal),
      limit: nf.format(limit),
    });
  }
  const displayCount = scopeTotal ?? analyzed;
  if (displayCount != null) {
    return t("welcome.hierarchicalTabReviewCount", {
      count: nf.format(displayCount),
    });
  }
  return null;
}
